import 'client-only';

import {
  SUBSCRIPTION_GROUP_IDS,
  SUBSCRIPTION_GROUPS,
} from '@/components/flow/steps/sms-verification/constants/constants';
import { type Brand } from '@/consts/brands';
import { get, patch, post } from '@/utils/http/client';
import { urls } from '@/utils/urls';

interface PhoneNumberObject {
  number: string;
  confirmedAt: string | null;
  unsubscribed: boolean;
}

const getIsPhoneNumberVerificationRequired = async (
  brand: Brand,
): Promise<boolean> =>
  get<boolean>(urls[brand].api.sms.requiresVerification).then((response) => {
    return response.data;
  });

const getPhoneNumberObject = async (brand: Brand): Promise<PhoneNumberObject> =>
  get<PhoneNumberObject>(urls[brand].api.phoneNumber.get).then((response) => {
    return response.data;
  });

const postPhoneNumberToTextConfirmationCode = (
  brand: Brand,
  phoneNumber: string,
) =>
  post(urls[brand].api.sms.sendConfirmation, {
    phoneNumber: phoneNumber,
  });

const postSmsPreferences = (brand: Brand, optIn: boolean) =>
  post(urls[brand].api.sms.preferences, {
    sms_transactional_enabled: optIn,
    sms_marketing_enabled: optIn,
  });

const postSmsMarketingDoubleOptIn = (brand: Brand, email: string | undefined) =>
  post(urls[brand].api.sms.doubleOptIn, {
    messageTypeId: SUBSCRIPTION_GROUP_IDS.smsMarketing,
    email: email,
  });

const postUnsubscribe = (brand: Brand, email: string | undefined) =>
  post(urls[brand].api.sms.unsubscribe, {
    subscriptionGroup: SUBSCRIPTION_GROUPS.messageType,
    subscriptionGroupId: SUBSCRIPTION_GROUP_IDS.smsTransactional,
    email: email,
  });

const postSmsSendMessage = (brand: Brand) =>
  post(urls[brand].api.sms.sendMessage, {
    type: 'signupPhotoUploadLink',
  });

const postConfirmationCode = (
  brand: Brand,
  confirmationCode: string,
): Promise<{ data: PhoneNumberObject }> =>
  post(urls[brand].api.sms.confirmToken, { token: confirmationCode });

const updatePhoneNumber = (brand: Brand, phoneNumber: string) =>
  patch(urls[brand].api.sms.updatePhoneNumber, { phone: phoneNumber });

export const PhoneNumberVerificationService = {
  postUnsubscribe,
  getIsPhoneNumberVerificationRequired,
  getPhoneNumberObject,
  postPhoneNumberToTextConfirmationCode,
  postSmsMarketingDoubleOptIn,
  postSmsPreferences,
  postSmsSendMessage,
  postConfirmationCode,
  updatePhoneNumber,
};
